import { useEffect, useMemo, useState } from 'react';
import { listMapStrategies } from '../liveSessionApi';
import { formatBuyType } from '../lib/formatters';
import type { StrategySummary } from '../types';
import styles from './NextStrategyPanel.module.css';

export type LiveStrategyDetail = {
  id: string;
  name: string;
  side: string;
  buyType: string;
};

export type NextStrategyPanelProps = {
  teamId: string;
  map: string;
  /** Current match side (T | CT); required to load applicable strategies. */
  side: string | null;
  isStrategist: boolean;
  selectedStrategyId: string | null;
  selectedStrategyName?: string | null;
  /** Server strategy for current open round (players + fallback). */
  liveStrategy?: LiveStrategyDetail | null;
  currentRoundNumber: number | null;
  busy: boolean;
  strategyNeedsConfirm: boolean;
  canConfirmStrategy: boolean;
  /** True when selected id matches last applied (strategist). */
  strategyConfirmed: boolean;
  onSelect: (strategy: StrategySummary) => void;
  onConfirm: () => void;
};

/** Preferred buy-type group order; unknown keys sort after these alphabetically. */
const BUY_ORDER = ['eco', 'half_buy', 'force_buy', 'full_buy', 'pistol', 'anti_eco', 'save'];

function normalizeBuyKey(buyType: string | null | undefined): string {
  const raw = (buyType ?? '').trim().toLowerCase().replace(/[-\s]+/g, '_');
  return raw || 'other';
}

function groupLabel(buyKey: string): string {
  if (buyKey === 'other') return 'Other';
  return formatBuyType(buyKey) || buyKey;
}

function groupStrategies(strategies: StrategySummary[]): { key: string; label: string; items: StrategySummary[] }[] {
  const map = new Map<string, StrategySummary[]>();
  for (const s of strategies) {
    const key = normalizeBuyKey(s.buyType);
    const list = map.get(key) ?? [];
    list.push(s);
    map.set(key, list);
  }
  for (const list of map.values()) {
    list.sort((a, b) => a.name.localeCompare(b.name));
  }
  const keys = Array.from(map.keys());
  keys.sort((a, b) => {
    const ia = BUY_ORDER.indexOf(a);
    const ib = BUY_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
  return keys.map((key) => ({
    key,
    label: groupLabel(key),
    items: map.get(key)!,
  }));
}

export default function NextStrategyPanel({
  teamId,
  map,
  side,
  isStrategist,
  selectedStrategyId,
  selectedStrategyName,
  liveStrategy = null,
  currentRoundNumber,
  busy,
  strategyNeedsConfirm,
  canConfirmStrategy,
  strategyConfirmed,
  onSelect,
  onConfirm,
}: NextStrategyPanelProps) {
  const [strategies, setStrategies] = useState<StrategySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const sideNorm = (side ?? '').trim().toUpperCase();
  const sideReady = sideNorm === 'T' || sideNorm === 'CT';

  useEffect(() => {
    if (!teamId || !map || !sideReady) {
      setStrategies([]);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const rows = await listMapStrategies(teamId, map, sideNorm);
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows : [];
        setStrategies(list.filter((s) => (s.status ?? '').toLowerCase() === 'active'));
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load strategies');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [teamId, map, sideNorm, sideReady]);

  const groups = useMemo(() => groupStrategies(strategies), [strategies]);

  const detail: LiveStrategyDetail | null = useMemo(() => {
    if (isStrategist) {
      if (selectedStrategyId) {
        const fromList = strategies.find((s) => s.id === selectedStrategyId);
        if (fromList) {
          return {
            id: fromList.id,
            name: fromList.name,
            side: fromList.side,
            buyType: fromList.buyType,
          };
        }
        if (selectedStrategyName) {
          return {
            id: selectedStrategyId,
            name: selectedStrategyName,
            side: '',
            buyType: '',
          };
        }
      }
      return null;
    }
    return liveStrategy ?? null;
  }, [isStrategist, selectedStrategyId, selectedStrategyName, strategies, liveStrategy]);

  const status: 'picking' | 'confirmed' | null = (() => {
    if (isStrategist) {
      if (strategyNeedsConfirm) return 'picking';
      if (strategyConfirmed && selectedStrategyId) return 'confirmed';
      return null;
    }
    if (liveStrategy) return 'confirmed';
    return null;
  })();

  const meta = currentRoundNumber != null ? `For R${currentRoundNumber + 1}` : 'Next round';

  const confirmTitle = canConfirmStrategy
    ? 'Lock in strategy for the next round'
    : 'Finish the current round first';

  const detailSideNorm = (detail?.side ?? '').toUpperCase();

  const handleSelectChange = (value: string) => {
    if (!value) return;
    const found = strategies.find((s) => s.id === value);
    if (found) onSelect(found);
  };

  return (
    <section className={styles.panel} aria-labelledby="next-strategy-heading">
      <div className={styles.head}>
        <h2 id="next-strategy-heading">Next Strategy</h2>
        <div className={styles.headMeta}>
          <span className={styles.meta}>{meta}</span>
          {status === 'picking' && (
            <span className={`${styles.status} ${styles.statusPicking}`}>Picking</span>
          )}
          {status === 'confirmed' && (
            <span className={`${styles.status} ${styles.statusConfirmed}`}>Confirmed</span>
          )}
        </div>
      </div>

      {loading && isStrategist && sideReady && (
        <p className={styles.empty}>Loading strategies…</p>
      )}
      {error && isStrategist && <p className={styles.error}>{error}</p>}

      <div className={styles.body}>
        <div className={styles.detail} data-testid="next-strategy-detail">
          {detail ? (
            <>
              <div className={styles.detailName}>{detail.name}</div>
              <div className={styles.tags}>
                {detailSideNorm && (
                  <span
                    className={`${styles.tag} ${
                      detailSideNorm === 'CT'
                        ? styles.tagCt
                        : detailSideNorm === 'T'
                          ? styles.tagT
                          : ''
                    }`}
                  >
                    {detailSideNorm}
                  </span>
                )}
                {detail.buyType && (
                  <span className={styles.tag}>{formatBuyType(detail.buyType)}</span>
                )}
              </div>
            </>
          ) : (
            <p className={styles.detailEmpty}>
              {isStrategist ? 'No strategy selected' : 'Waiting for strategist'}
            </p>
          )}
        </div>

        {isStrategist && !sideReady && (
          <p className={styles.emptyInline}>Set starting side to load strategies</p>
        )}

        {isStrategist && sideReady && !loading && !error && strategies.length === 0 && (
          <p className={styles.emptyInline}>No strategies for this map and side</p>
        )}

        {isStrategist && sideReady && strategies.length > 0 && (
          <div className={styles.pickerRow}>
            <label className={styles.srOnly} htmlFor="next-strategy-select">
              Choose strategy
            </label>
            <div className={styles.selectWrap}>
              <select
                id="next-strategy-select"
                className={styles.select}
                value={selectedStrategyId ?? ''}
                disabled={busy}
                onChange={(e) => handleSelectChange(e.target.value)}
              >
                <option value="">Select strategy…</option>
                {groups.map((g) => (
                  <optgroup key={g.key} label={g.label}>
                    {g.items.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <span className={styles.selectChevron} aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </span>
            </div>
            <button
              type="button"
              className={styles.lockIn}
              disabled={busy || !strategyNeedsConfirm || !canConfirmStrategy}
              title={confirmTitle}
              onClick={onConfirm}
            >
              Lock-in
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
